import { useEffect, useState } from "react";
import {
  ResponsiveContainer,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Bar,
  BarChart,
  LineChart,
  Line,
} from "recharts";
import ReportItem from "../ReportItem";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Skeleton } from "primereact/skeleton";
import Icon from "components/Icon";
import TableBlock from "./TableBlock";
import { MultiSelect } from "primereact/multiselect";
import { useReportContext } from "context/ReportContext";

function Wrapper({ children, container, ...other }) {
  if (container === false) return <div {...other}>{children}</div>;
  return <ReportItem {...other}>{children}</ReportItem>;
}

export default function SearchesBlock({
  id,
  title,
  description,
  data,
  cols,
  colors = [],
  loading,
  sortField,
  sortOrder,
  footer,
  fields,
  container = true,
  className,
  swap,
  heading,
}) {
  const chartOptions = {
    indexAxis: "y",
    interaction: {
      intersect: false,
      mode: "nearest",
      axis: "y",
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          display: false,
        },
      },
      x: {
        display: false,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "rgba(0,0,0,0)",
        bodyColor: "#000",
        yAlign: "center",
        xAlign: "left",
        callbacks: {
          title: function (context) {
            return "";
          },
        },
      },
    },
  };

  let [barChartData, setBarChartData] = useState({});

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="chart-tooltip">
          <p className="chart-label">{`${payload[0].value}`}</p>
        </div>
      );
    }
  };

  const formatNum = (num) => {
    return num.toLocaleString();
  };

  const sortData = (data, field, order) => {
    if (order === 1) {
      return data.sort((a, b) => a[field] - b[field]);
    } else {
      return data.sort((a, b) => b[field] - a[field]);
    }
  };

  const {
    generateHcs,
    report,
    cat,
    subCat,
    type,
    selectedCat,
    selectedSubCat,
    selectedType,
    setSelectedCat,
    setSelectedSubCat,
    setSelectedType,
  } = useReportContext();

  useEffect(() => {
    generateHcs(report);
  }, [selectedCat, selectedSubCat, selectedType]);

  return (
    <Wrapper id={id} container={container} className={className}>
      <div className="report-item__heading">
        {!heading && <h2 className="report-item__heading-title">{title}</h2>}
        {heading && <h3 className="report-item__heading-subtitle">{title}</h3>}
      </div>
      {description && (
        <p className="report-item__heading-description">{description}</p>
      )}
      <div
        className="report-item__data showChart"
        style={{ gridTemplateColumns: "1fr" }}
      >
        <div className="report-item__data-filters">
          <div className="report-item__data-filters-group">
            <div className="report-item__data-filters-title">Category</div>
            <MultiSelect
              inputId="filter-cat"
              value={selectedCat}
              onChange={(e) => {
                setSelectedCat(e.value);
                setSelectedSubCat([]);
                setSelectedType([]);
              }}
              showClear
              options={cat}
              placeholder="All"
              maxSelectedLabels={1}
            />
          </div>
          <div className="report-item__data-filters-group">
            <div className="report-item__data-filters-title">Sub Category</div>
            <MultiSelect
              inputId="filter-subCat"
              value={selectedSubCat}
              onChange={(e) => { setSelectedSubCat(e.value); setSelectedCat([]); setSelectedType([]);}}
              showClear
              options={subCat}
              placeholder="All"
              maxSelectedLabels={1}
            />
          </div>
          <div className="report-item__data-filters-group">
            <div className="report-item__data-filters-title">Type</div>
            <MultiSelect
              inputId="filter-type"
              value={selectedType}
              onChange={(e) => { setSelectedType(e.value); setSelectedCat([]); setSelectedSubCat([]);}}
              showClear
              options={type}
              placeholder="All"
              maxSelectedLabels={1}
            />
          </div>
        </div>
        {data && (
          <div className="report-item__data-graph">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart
                data={data}
                key={1}
                margin={{ top: 10, right: 20, bottom: 0, left: 0 }}
              >
                <CartesianGrid vertical={false} stroke="#E5E5E9" />
                <XAxis
                  dataKey={sortField}
                  tickFormatter={(value) => value.substring(0, 3)}
                />
                <YAxis
                  tickFormatter={(value) => {
                    if (value >= 1000000000) {
                      return (
                        (value % 1000000000 === 0
                          ? value / 1000000000
                          : (value / 1000000000).toFixed(1)) + "B"
                      );
                    } else if (value >= 1000000) {
                      return (
                        (value % 1000000 === 0
                          ? value / 1000000
                          : (value / 1000000).toFixed(1)) + "M"
                      );
                    } else if (value >= 1000) {
                      return (
                        (value % 1000 === 0
                          ? value / 1000
                          : (value / 1000).toFixed(1)) + "K"
                      );
                    } else {
                      return value.toLocaleString();
                    }
                  }}
                />
                <Tooltip
                  formatter={(value) => {
                    return value.toLocaleString();
                  }}
                />
                <Legend />
                <Line
                  name="Previous 12 Months"
                  type="linear"
                  dataKey="previous"
                  stroke="#076CDF"
                  dot={{ r: 5, fill: "#076CDF" }}
                  legendType="circle"
                />
                <Line
                  name="Last 12 Months"
                  type="linear"
                  dataKey="last"
                  stroke="#9747FF"
                  dot={{ r: 5, fill: "#9747FF" }}
                  legendType="circle"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
        <TableBlock
          data={data}
          cols={cols}
          colors={colors}
          fields={fields}
          // sortField={sortField}
          sortOrder={sortOrder}
          footer={footer}
          loading={loading}
          container={false}
          swap={swap}
        />
      </div>
    </Wrapper>
  );
}
